import type NewRelicBrowser from 'new-relic-browser'
import newrelic from 'newrelic'

declare global {
  interface Window {
    newrelic?: typeof NewRelicBrowser
  }
}

// Plain objects thrown by Next.js internals stringify to "[object Object]";
// pull their `.message` so logs and New Relic show the real error.
const getErrorMessage = (err: unknown): string => {
  if (err instanceof Error) return err.message
  if (typeof err === 'object' && err !== null && 'message' in err) {
    return String((err as { message: unknown }).message)
  }
  return String(err)
}

/**
 * A utility for sending error logs to New Relic that
 * works on the Next.js server or the browser.
 *
 * @param err The error instance to log
 * @param props Optional extra attributes to send to New Relic
 */
export const reportError = (err: unknown, props?: Record<string, string>) => {
  // eslint-disable-next-line no-console
  console.error(
    JSON.stringify({
      err: getErrorMessage(err),
      stack: err instanceof Error ? err.stack : undefined,
      props,
    }),
  )

  // Convert unknown to Error for newrelic API
  const error = err instanceof Error ? err : new Error(getErrorMessage(err))

  if (typeof window === 'undefined') {
    newrelic.noticeError(error, props)
  } else {
    window.newrelic?.noticeError(error, props)
  }
}
